import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { BASE_PATH } from '@/constants/config';
import {
  isRecaptchaV3LoadTimeoutError,
  useRecaptcha,
} from '@/hooks/use-recaptcha';
import { ERROR_CODES, readErrorCode, readErrorMessage } from '@common/utils';

import { AuthError, type LoginSuccess, loginSuccessSchema } from './contract';

/** Exported for tests: the payload is untrusted, so its parsing is the risky part. */
export function toLoginError(payload: unknown): AuthError {
  const code = readErrorCode(payload);
  const message = readErrorMessage(payload);

  switch (code) {
    case ERROR_CODES.CAPTCHA_CHALLENGE_REQUIRED:
    case ERROR_CODES.SSO_REQUIRED:
      return new AuthError(code, message);
    case ERROR_CODES.ACCOUNT_LOCKED:
      return new AuthError(
        code,
        message ?? 'Too many failed attempts. Please try again in 10 minutes.',
      );
    default:
      return new AuthError(ERROR_CODES.LOGIN_FAILED, message ?? 'Login failed');
  }
}

/**
 * The login form's schema and submit mutation, shaped like the researcher
 * signup pair in `routes/researchers/-auth/mutation.ts`.
 *
 * The CAPTCHA handshake lives here rather than in the page: acquiring the v3
 * token is part of submitting, and folding a v3 load timeout into
 * `CAPTCHA_CHALLENGE_REQUIRED` means the page has one question to ask
 * ("is the challenge required?") instead of tracking a flag, a token, and two
 * error shapes that all meant the same thing.
 */
export const useLoginMutation = () => {
  const { t } = useLingui();
  const { getToken } = useRecaptcha();

  const schema = z.object({
    email: z
      .email(t`Please enter a valid email address`)
      .trim()
      .min(1, t`Email is required`),
    password: z.string().min(8, t`Password must be at least 8 characters`),
  });

  type FormFields = z.infer<typeof schema>;

  type LoginVariables = {
    fields: FormFields;
    /** Present once the user has solved the v2 checkbox fallback. */
    captchaV2Token?: string;
  };

  const mutation = useMutation<LoginSuccess, AuthError, LoginVariables>({
    mutationFn: async ({ fields, captchaV2Token }) => {
      let captchaToken: string;
      if (captchaV2Token) {
        captchaToken = 'v2-fallback';
      } else {
        try {
          captchaToken = await getToken('login');
        } catch (error) {
          if (isRecaptchaV3LoadTimeoutError(error)) {
            throw new AuthError(ERROR_CODES.CAPTCHA_CHALLENGE_REQUIRED);
          }
          throw new AuthError(
            ERROR_CODES.LOGIN_FAILED,
            'An unexpected error occurred',
          );
        }
      }

      const response = await fetch(`${BASE_PATH}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: fields.email.trim(),
          password: fields.password,
          captchaToken,
          ...(captchaV2Token && { captchaV2Token }),
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw toLoginError(await response.json());
      }

      // Validate at the boundary so a malformed success response is a hard
      // failure rather than a redirect to an undefined destination.
      const parsed = loginSuccessSchema.safeParse(await response.json());
      if (!parsed.success) {
        throw new AuthError(
          ERROR_CODES.LOGIN_FAILED,
          'Invalid response from authentication service',
        );
      }
      return parsed.data;
    },
  });

  return { mutation, schema };
};
